import { Box, Grid, Typography, alpha, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isEUCountry, resolveShipmentValue } from '../../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../../utils/currencyConversion';
import { getCountryName } from '../../../../utils/exportShipment';
import Joi from 'joi';
import React, { useEffect, useMemo, useState } from 'react';
import SbTextField from '../../../../components/Textfield';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { BoxErrorType, BoxType, createShipmentContextType } from '../../../../types/shipment';
import { documentWeight } from '../../../GetRatesV2/RatesInput/Schema';
import { defaultBoxesErrorState, defaultProductErrorState } from '../../constants';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { getBoxDimensionsByAWB, PrevBoxDimensions } from '../../../../utils/getPreviousBoxDimensions';

const BoxDimensions = ({ writePermission, isDisabled }: { writePermission: boolean; isDisabled: boolean }) => {
  const theme = useTheme();
  const {
    selectedBox,
    setSelectedBox,
    boxes,
    setBoxes,
    shipmentType,
    setBoxValidation,
    consignee,
    totalShipmentValue,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment?.shipmentById);
  const [errors, setErrors] = useState<BoxErrorType>({ ...defaultBoxesErrorState });
  const [placeholders, setPlaceHolders] = useState<{ lengthUnit: string; weightUnit: string }>({
    lengthUnit: 'In cm',
    weightUnit: 'In Kg',
  });
  const [prevDimensions, setPrevDimensions] = useState<PrevBoxDimensions | null>(null);

  const destCountry = useMemo(() => {
    return (
      (consignee?.country as any)?.name ||
      consignee?.country ||
      getCountryName(consignee) ||
      getCountryName(shipmentById?._consigneeId) ||
      ''
    );
  }, [consignee, shipmentById]);

  const isDestinationEU = useMemo(() => isEUCountry(destCountry), [destCountry]);
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentType?.currency, isEU: isDestinationEU });

  const resolvedShipmentValue = useMemo(() => {
    return resolveShipmentValue(boxes, totalShipmentValue);
  }, [boxes, totalShipmentValue]);

  const eurEquivalentText = useMemo(() => {
    if (!isDestinationEU || !eurRate || (shipmentType?.currency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (resolvedShipmentValue * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isDestinationEU, eurRate, shipmentType?.currency, resolvedShipmentValue]);

  const { length, width, height, weight } = selectedBox || {};
  const { unit, type } = shipmentType;

  useEffect(() => {
    if (unit) getPlaceholder();
  }, [unit]);

  const weightErrForDocument = useMemo(
    () => type === 'document' && Number(weight) > documentWeight[unit],
    [type, weight, unit],
  );

  useEffect(() => {
    if (weightErrForDocument) {
      setBoxValidation(true);
    }
  }, [weightErrForDocument]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    validateProperty(event);
    const name = event.target.name as keyof BoxType;
    const value = event.target.value;
    const boxesTemp = [...boxes];
    const ind = boxesTemp.findIndex(box => box.id === selectedBox.id);
    const selectedBoxIndex = ind > -1 ? ind : boxes.length + 1;
    const boxDetails = { ...boxesTemp[selectedBoxIndex] };
    if (name === 'length' || name === 'width' || name === 'height' || name === 'weight') boxDetails[name] = value;
    setSelectedBox(boxDetails);
    boxesTemp.splice(selectedBoxIndex, 1, boxDetails);
    setBoxes([...boxesTemp]);
  }

  const schema = Joi.object().keys({
    id: Joi.string(),
    height: Joi.number().min(1).optional().allow('', null).messages({ 'number.min': 'Value should be greater than 1' }),
    width: Joi.number().min(1).optional().allow('', null).messages({ 'number.min': 'Value should be greater than 1' }),
    length: Joi.number().min(1).optional().allow('', null).messages({ 'number.min': 'Value should be greater than 1' }),
    weight: Joi.number()
      .min(0.01)
      .required()
      .messages({
        'number.base': `Please enter Weight`,
        'number.min':
          type === 'document'
            ? `For shipment type document Weight should be less than ${documentWeight[unit]} ${unit}`
            : 'Weight should be greater than 0',
      }),
    actions: Joi.number().min(1).optional().messages({ 'number.min': 'Value should be greater than 1' }),
  });

  function validateProperty(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    const obj = { [name]: value };
    const result = schema.validate(obj);
    const { error } = result;
    const errorData = { ...errors };
    const errorMessage = error && error.details[0].context?.key === name ? error.details[0].message : null;
    if (errorMessage && (name in defaultBoxesErrorState || name in defaultProductErrorState)) {
      errorData[name as keyof BoxErrorType] = errorMessage;
    } else {
      errorData[name as keyof BoxErrorType] = '';
    }
    setErrors(errorData);
  }

  function getPlaceholder() {
    if (unit === 'kg-cm') {
      setPlaceHolders({
        lengthUnit: 'cm',
        weightUnit: 'kg',
      });
    } else {
      setPlaceHolders({
        lengthUnit: 'in',
        weightUnit: 'lb',
      });
    }
  }

  useEffect(() => {
    if (shipmentById?._packageId?.atIntransit?.box) {
      const result =
        selectedBox.childAWBNumber !== undefined
          ? getBoxDimensionsByAWB(shipmentById?._packageId.atIntransit, selectedBox.childAWBNumber)
          : null;

      if (result) {
        setPrevDimensions(result);
      }
    }
  }, [shipmentById, selectedBox.childAWBNumber]);

  return (
    <Grid container spacing={2}>
      <Grid item md={3} xs={6}>
        <SbTextField
          // placeholder={placeholders.lengthUnit}
          label="Length"
          onChange={handleChange}
          name="length"
          value={length}
          type="number"
          error={errors.length}
          endAdornment={placeholders.lengthUnit}
          disabled={type === 'document' || !writePermission || isDisabled}
          // disabled={!writePermission}
          // showInputProps={false}
        />
        {prevDimensions?.length !== undefined && prevDimensions?.length !== null && (
          <Typography variant="caption" color="textSecondary">
            Previous Length: {prevDimensions.length} {prevDimensions.unitOfMeasurement}
          </Typography>
        )}
      </Grid>
      <Grid item md={3} xs={6}>
        <SbTextField
          // placeholder={placeholders.lengthUnit}
          label="Width"
          onChange={handleChange}
          name="width"
          value={width}
          type="number"
          error={errors.width}
          endAdornment={placeholders.lengthUnit}
          disabled={type === 'document' || !writePermission || isDisabled}
          // showInputProps={false}
        />
        {prevDimensions?.width !== undefined && prevDimensions?.width !== null && (
          <Typography variant="caption" color="textSecondary">
            Previous Width: {prevDimensions.width} {prevDimensions.unitOfMeasurement}
          </Typography>
        )}
      </Grid>
      <Grid item md={3} xs={6}>
        <SbTextField
          // placeholder={placeholders.lengthUnit}
          label="Height"
          onChange={handleChange}
          name="height"
          value={height}
          type="number"
          error={errors.height}
          endAdornment={placeholders.lengthUnit}
          disabled={type === 'document' || !writePermission || isDisabled}
          // showInputProps={false}
        />
        {prevDimensions?.height !== undefined && prevDimensions?.height !== null && (
          <Typography variant="caption" color="textSecondary">
            Previous Height: {prevDimensions.height} {prevDimensions.unitOfMeasurement}
          </Typography>
        )}
      </Grid>
      <Grid item md={3} xs={6}>
        <SbTextField
          // placeholder={placeholders.weightUnit}
          label="Weight"
          onChange={handleChange}
          name="weight"
          required
          value={weight}
          type="number"
          error={
            weightErrForDocument
              ? `For shipment type document Weight should be less than ${documentWeight[unit]} ${unit}`
              : errors.weight
          }
          endAdornment={placeholders.weightUnit}
          disabled={!writePermission || isDisabled}
        />
        {prevDimensions?.weight !== undefined && prevDimensions?.weight !== null && (
          <Typography variant="caption" color="textSecondary">
            Previous Weight: {prevDimensions.weight} {prevDimensions.unitOfMeasurement}
          </Typography>
        )}
      </Grid>
      {isDestinationEU && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '8px',
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
              Goods value {shipmentType?.currency || 'INR'} {resolvedShipmentValue.toFixed(2)}
              {eurEquivalentText ? ` ${eurEquivalentText}` : ''} · €3 duty per line
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default BoxDimensions;
